import type { SectionProps } from '@/types';

const DAY_LABELS: Record<string, string> = {
  mon: 'Mon',
  tue: 'Tue',
  wed: 'Wed',
  thu: 'Thu',
  fri: 'Fri',
  sat: 'Sat',
  sun: 'Sun',
};

function formatTime(iso: string | null): string {
  if (!iso) return '';
  const match = iso.match(/(\d{2}):(\d{2})/);
  if (!match) return iso;
  const hours = parseInt(match[1], 10);
  const mins = match[2];
  const period = hours >= 12 ? 'PM' : 'AM';
  const h12 = hours % 12 || 12;
  return `${h12}:${mins} ${period}`;
}

export function AboutSection({ business, config }: SectionProps) {
  const title = (config.title as string) || 'About us';
  const text = business.description || (config.body as string);

  if (!text) return null;

  return (
    <section className="section-card">
      <h2 className="text-xl font-semibold text-slate-900">{title}</h2>
      <p className="mt-4 whitespace-pre-line leading-relaxed text-slate-600">{text}</p>

      {business.address_line && (
        <div className="mt-6 flex items-start gap-3 rounded-lg bg-slate-50 p-4">
          <svg className="mt-0.5 h-5 w-5 shrink-0 text-brand-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
          <div>
            <p className="font-medium text-slate-900">Address</p>
            <p className="text-sm text-slate-600">
              {business.address_line}
              {business.pincode ? `, ${business.pincode}` : ''}
            </p>
          </div>
        </div>
      )}

      {business.hours.length > 0 && (
        <div className="mt-6">
          <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-500">Opening hours</h3>
          <ul className="mt-3 divide-y divide-slate-100">
            {business.hours.map((h) => (
              <li key={h.day_of_week} className="flex justify-between py-2 text-sm">
                <span className="font-medium capitalize text-slate-700">
                  {DAY_LABELS[h.day_of_week.slice(0, 3)] ?? h.day_of_week}
                </span>
                <span className="text-slate-600">
                  {h.is_closed
                    ? 'Closed'
                    : `${formatTime(h.open_time)} – ${formatTime(h.close_time)}`}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}
